import type { ShipListState } from "@/frontend/types";
import { renameTableCols } from "@/frontend/utils/renamedTableCols";
import { defineStore } from "pinia";

export enum WishlistTableViewImageColsEnum {
  STORE_IMAGE = "storeImage",
  STORE_IMAGE_WIDE = "storeImageWide",
  ANGLED_VIEW = "angledView",
  ANGLED_VIEW_WIDE = "angledViewWide",
}

export enum WishlistTableViewColsEnum {
  MANUFACTURER_NAME = "modelManufacturerName",
  LENGTH = "modelLength",
  BEAM = "modelBeam",
  HEIGHT = "modelHeight",
  MASS = "modelMass",
  CARGO = "modelCargo",
  CREW = "modelCrew",
  SCM_SPEED = "modelScmSpeed",
  MAX_SPEED = "modelMaxSpeed",
  GROUND_MAX_SPEED = "modelGroundMaxSpeed",
  FOCUS = "modelFocus",
  PRODUCTION_STATUS = "modelProductionStatus",
  PRICE = "modelPrice",
  PLEDGE_PRICE = "modelPledgePrice",
}

// Every sort the wishlist can offer as a chip. No custom order: that is the
// hangar's, and a wishlist has none of its own.
export enum WishlistSortFieldsEnum {
  NAME = "name",
  MANUFACTURER_NAME = "modelManufacturerName",
  LENGTH = "modelLength",
  BEAM = "modelBeam",
  HEIGHT = "modelHeight",
  MASS = "modelMass",
  CARGO = "modelCargo",
  SCM_SPEED = "modelScmSpeed",
  MAX_SPEED = "modelMaxSpeed",
  GROUND_MAX_SPEED = "modelGroundMaxSpeed",
  PRODUCTION_STATUS = "modelProductionStatus",
  PRICE = "modelPrice",
  PLEDGE_PRICE = "modelPledgePrice",
}

interface WishlistState extends ShipListState {
  ships: string[];
  tableViewImageCols: WishlistTableViewImageColsEnum[];
  tableViewCols: WishlistTableViewColsEnum[];
  sortFields: WishlistSortFieldsEnum[];
}

export const useWishlistStore = defineStore("wishlist", {
  state: (): WishlistState => ({
    detailsVisible: false,
    filterVisible: true,
    gridView: true,
    ships: [],
    tableViewImageCols: [WishlistTableViewImageColsEnum.STORE_IMAGE],
    tableViewCols: [WishlistTableViewColsEnum.MANUFACTURER_NAME],
    sortFields: [
      WishlistSortFieldsEnum.NAME,
      WishlistSortFieldsEnum.MANUFACTURER_NAME,
      WishlistSortFieldsEnum.LENGTH,
      WishlistSortFieldsEnum.CARGO,
      WishlistSortFieldsEnum.PRICE,
      WishlistSortFieldsEnum.PRODUCTION_STATUS,
    ],
  }),
  getters: {
    empty(state) {
      return state.ships.length === 0;
    },
  },
  actions: {
    toggleDetails() {
      this.detailsVisible = !this.detailsVisible;
    },
    toggleFilter() {
      this.filterVisible = !this.filterVisible;
    },
    save(payload: string[]) {
      this.ships = payload;
    },
    add(payload: string) {
      this.ships.push(payload);
    },
    remove(payload: string) {
      this.ships.splice(this.ships.indexOf(payload), 1);
    },
    setTableViewCols(cols: WishlistTableViewColsEnum[]) {
      this.tableViewCols = cols;
    },
    setSortFields(fields: WishlistSortFieldsEnum[]) {
      this.sortFields = fields;
    },
  },
  persist: {
    pick: [
      "ships",
      "detailsVisible",
      "gridView",
      "tableViewImageCols",
      "tableViewCols",
      "sortFields",
    ],
    afterHydrate: ({ store }) => {
      store.tableViewCols = renameTableCols<WishlistTableViewColsEnum>(
        store.tableViewCols,
        {
          modelMinCrew: WishlistTableViewColsEnum.CREW,
          modelMaxCrew: WishlistTableViewColsEnum.CREW,
        },
      );
    },
  },
});
